import React, { useEffect, useMemo, useState } from 'react'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory from 'react-bootstrap-table2-paginator'
import CSSModules from 'react-css-modules'
import { injectIntl } from 'react-intl'
import { connect } from 'react-redux'
import { AppWrapper, Block, Spinner } from 'components'
import Breadcrumbs from 'components/BreadCrumbs/BreadCrumbs'
import AuthProtected from 'containers/AuthProtected/AuthProtected'
import { actions as couponPointsTransactionActions } from 'coupon-common/src/modules/admin/pointsManager/couponPointsTransactions';
import { actions as pointsBalancesActions } from 'coupon-common/src/modules/admin/pointsManager/pointsBalances';
import { actions as pointsTransactionsActions } from 'coupon-common/src/modules/admin/pointsManager/pointsTransactions';
import messages from './messages'
import styles from './PointsTransactionsTable.module.scss'

const AVATAR_VARIANT_COUNT = 6;

// Deterministically picks an avatar color variant + initials for a user identifier
const getAvatarProps = (identifier) => {
    const safeId = identifier || '?';
    let hash = 0;
    for (let i = 0; i < safeId.length; i += 1) {
        hash = (hash * 31 + safeId.charCodeAt(i)) % 997;
    }
    const variant = hash % AVATAR_VARIANT_COUNT;

    const namePart = safeId.includes('@') ? safeId.split('@')[0] : safeId;
    const segments = namePart.split(/[.\s_-]+/).filter(Boolean);
    const initials = segments.length > 1
        ? `${segments[0][0]}${segments[1][0]}`
        : namePart.slice(0, 2);

    return { variant, initials: initials.toUpperCase() };
};

// Turns "payment_by_points" into "Payment by points"
const humanizeNotes = (value) => {
    if (!value) return '';
    const spaced = String(value).replace(/_/g, ' ').toLowerCase();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const STATUS_VARIANTS = {
    success: 'success',
    approved: 'success',
    active: 'success',
    pending: 'pending',
    waiting: 'pending',
    failed: 'failed',
    rejected: 'failed',
};

const PointsTransactionsTable = ({ intl, dispatch, transactionsData, transactionsDataFetched, couponsData, couponsDataFetched, balancesData, balancesDataFetched, currentUserEmail, currentPointsBalance }) => {

    const [searchQuery, setSearchQuery] = useState(null);
    const [data, setData] = useState(null)
    const [couponData, setCouponData] = useState(null);
    const [combinedData, setCombinedData] = useState([]);
    const [balances, setBalances] = useState([]);
    const [loading, setLoading] = useState(true);


    useEffect(() => {
        dispatch(pointsTransactionsActions.getPointsTransactions())
        dispatch(couponPointsTransactionActions.getCouponTransactions({}));
        dispatch(pointsBalancesActions.getPointsBalances())
    }, [dispatch])

     useEffect(() => {
        if(transactionsDataFetched){
            const transactions = transactionsData?.map((tx)=> (
                {...tx, id: `P${String(tx.id).padStart(4, '0')}`}))
            setData(transactions)
        }
        }, [transactionsData, transactionsDataFetched])

        useEffect(() => {
            if(balancesDataFetched){
                setBalances(balancesData)
            }
        }, [balancesData, balancesDataFetched])


        useEffect(() => {
            if(couponsDataFetched){
                // ✅ Transform `couponPointsTransactions` to match `pointsTransactions`
                const mappedCouponTransactions = couponsData?.results?.map(tx => ({
                    id: `C${String(tx.id).padStart(4, '0')}`,
                    time: tx.transaction_datetime,
                    amount: tx.points_delta, // Ensure it's a number
                    from_user:  tx.from_email && tx.from_email.trim() !== "" ? tx.from_email : "ADMIN", // Use "ADMIN" if no from email
                    to_user: tx.to_email,
                    status: "success", // Assume success
                    notes: tx.transaction_type,
                    previous_balance: null, // No equivalent field
                    new_balance: null, // No equivalent field
                    from_user_name: tx.from_email && tx.from_email.trim() !== "" ? tx.from_user_name : "ADMIN",
                    to_user_name: tx.to_user_name,
                }));
                setCouponData(mappedCouponTransactions);
            }
        }, [couponsData, couponsDataFetched])

        const fillMissingBalances = (transactions) => {
            // Sort transactions from newest to oldest (latest transactions first)
            transactions.sort((a, b) => new Date(b.time) - new Date(a.time));

            // Get the latest available balance for the current user, ensuring it's a number
            let currentBalance = Number(currentPointsBalance) || 0;

            // Iterate from newest to oldest transaction
            for (let i = 0; i < transactions.length; i++) {
                let transaction = transactions[i];
                const { to_user, from_user } = transaction;

                // Ensure `amount` is a valid number
                let amount = Number(transaction.amount) || 0;

                if (to_user === currentUserEmail) {
                    // If the user is **receiving** points
                    transaction.new_balance = currentBalance;
                    transaction.previous_balance = Number((currentBalance - amount).toFixed(2));
                    currentBalance = transaction.previous_balance; // Update balance for next transaction

                } else if (from_user === currentUserEmail) {
                    // If the user is **sending** points
                    transaction.new_balance = currentBalance;
                    transaction.previous_balance = Number((currentBalance + amount).toFixed(2));
                    currentBalance = transaction.previous_balance; // Update balance for next transaction

                    // Ensure the amount being given away is displayed as negative
                    transaction.amount = -Math.abs(amount);
                }
            }

            return transactions; // Already sorted from newest to oldest
        };


        // This grabs the points transactions related to coupons that give back coupons
        // or points from referrals. They are pulled from the referral_program_pointstransactions
        useEffect(() => {
            if (data && couponData && balancesDataFetched) {
                const mergedData = [...data, ...couponData];
                mergedData.sort((a, b) => new Date(b.time) - new Date(a.time));
                const filterByCurrentUser = mergedData.filter((data)=> data.from_user == currentUserEmail || data.to_user == currentUserEmail)

                const updatedTransactions = fillMissingBalances(filterByCurrentUser);
                setCombinedData(updatedTransactions);
                setLoading(false);
            }
        }, [data, couponData, balancesDataFetched]);

    // Summary stats shown in the card header
    const { pointsIn, pointsOut, netChange } = useMemo(() => (
        combinedData.reduce((acc, tx) => {
            const amount = Number(tx.amount) || 0;
            if (amount >= 0) {
                acc.pointsIn += amount;
            } else {
                acc.pointsOut += amount;
            }
            acc.netChange += amount;
            return acc;
        }, { pointsIn: 0, pointsOut: 0, netChange: 0 })
    ), [combinedData]);

    const renderAvatar = (identifier, fallbackLabel) => {
        const { variant, initials } = getAvatarProps(identifier || fallbackLabel);
        return (
            <span className={`${styles.avatar} ${styles[`avatar--${variant}`]}`}>
                {initials}
            </span>
        );
    };

    const columns = [
        {
            dataField: 'id',
            text: intl.formatMessage(messages.idColumnText),
            sort: true,
            headerClasses: styles['sticky-header'],
        },
        {
            dataField: 'time',
            text: intl.formatMessage(messages.timeColumnText),
            sort: true,
            headerClasses: styles['sticky-header'],
            formatter: (cellContent) => {
                // Convert the string into a Date object and format it
                const formattedDate = new Date(cellContent).toLocaleString();
                return formattedDate || cellContent;
            },
        },
        {
            dataField: 'from_user',
            text: intl.formatMessage(messages.fromUserColumnText),
            sort: true,
            headerClasses: styles['sticky-header'],
            formatter: (cell, row) => {
                const label = (row.from_user === currentUserEmail || row.from_user === "info@mycouponbook.ca") ? row.from_user : row.from_user_name;
                return (
                    <span className={styles['user-cell']}>
                        {renderAvatar(row.from_user, label)}
                        {label}
                    </span>
                );
            },
        },
        {
            dataField: 'to_user_name',
            text: intl.formatMessage(messages.toUserColumnText),
            sort: true,
            headerClasses: styles['sticky-header'],
            formatter: (cell, row) => {
                const label = row.to_user === currentUserEmail ? row.to_user : row.to_user_name;
                if (!label) return null;
                return (
                    <span className={styles['user-cell']}>
                        {renderAvatar(row.to_user, label)}
                        {label}
                    </span>
                );
            },
        },
        {
            dataField: 'previous_balance',
            text: intl.formatMessage(messages.previousBalanceColumnText),
            sort: true,
            headerClasses: styles['sticky-header'],
        },
        {
            dataField: 'amount',
            text: intl.formatMessage(messages.amountColumnText),
            sort: true,
            headerClasses: styles['sticky-header'],
            formatter: (cell, row) => {
                const amount = Number(row.amount) || 0;
                const isPositive = row?.to_user === currentUserEmail && amount > 0;
                return (
                    <span className={`${styles['amount-pill']} ${isPositive ? styles.positive : styles.negative}`}>
                        {isPositive ? '▲' : '▼'} {Math.abs(amount)}
                    </span>
                );
            },
        },
        {
            dataField: 'new_balance',
            text: intl.formatMessage(messages.newBalanceColumnText),
            sort: true,
            headerClasses: styles['sticky-header'],
        },
        {
            dataField: 'status',
            text: intl.formatMessage(messages.statusColumnText),
            headerClasses: styles['sticky-header'],
            formatter: (cell) => {
                const variant = STATUS_VARIANTS[String(cell).toLowerCase()] || 'default';
                return (
                    <span className={`${styles['status-badge']} ${styles[`status-badge--${variant}`]}`}>
                        <span className={styles['status-dot']} />
                        {cell}
                    </span>
                );
            },
        },
        {
            dataField: 'notes',
            text: intl.formatMessage(messages.notesColumnText),
            sort: true,
            headerClasses: styles['sticky-header'],
            formatter: (cell) => <span className={styles['notes-text']}>{humanizeNotes(cell)}</span>,
        },
    ];

    const paginationOptions = {
        sizePerPage: 25,
        totalSize: combinedData?.length,
        showTotal: true,
        sizePerPageList: [10, 25, 50, 100],
        firstPageText: 'First',
        lastPageText: 'Last',
        nextPageText: 'Next',
        prePageText: 'Previous',
        withFirstAndLast: true,
    };



    return (
        <AppWrapper
            showHeader
            showSideNav
            showHeadingRow
            headingText={intl.formatMessage(messages.pageHeadingText)}
        >
            <AuthProtected />

            <div className="col-md-12">
                {/* <Breadcrumbs locationArray={["Admin", "Points Manager", "Points Transactions"]} /> */}
                <Block>
                    {(loading) ? (
                        <Spinner show />
                    ) : (
                        <div className={styles['table-card']}>
                            <div className={styles['table-card-header']}>
                                <h2 className={styles['table-card-title']}>
                                    {intl.formatMessage(messages.pageHeadingText)}
                                </h2>
                                <div className={styles['stat-pills']}>
                                    <span className={`${styles['stat-pill']} ${styles['stat-pill--in']}`}>
                                        {intl.formatMessage(messages.pointsInLabel)} <strong>+{Number(pointsIn.toFixed(2))}</strong>
                                    </span>
                                    <span className={`${styles['stat-pill']} ${styles['stat-pill--out']}`}>
                                        {intl.formatMessage(messages.pointsOutLabel)} <strong>{Number(pointsOut.toFixed(2))}</strong>
                                    </span>
                                    <span className={`${styles['stat-pill']} ${styles['stat-pill--net']}`}>
                                        {intl.formatMessage(messages.netChangeLabel)} <strong>{netChange >= 0 ? '+' : ''}{Number(netChange.toFixed(2))}</strong>
                                    </span>
                                </div>
                            </div>

                            <div className={`${styles['table-container']} table-container overflow-auto`}>
                                {combinedData.length === 0 ? (
                                    <div className={styles['empty-state']}>
                                        {intl.formatMessage(messages.noTransactionsText)}
                                    </div>
                                ) : (
                                    <BootstrapTable
                                        classes="table-borderless mb-0 overflow-auto custom-react-bootstrap-table"
                                        striped
                                        bordered={false}
                                        bootstrap4
                                        keyField="id"
                                        data={combinedData}
                                        columns={columns}
                                        pagination={paginationFactory(paginationOptions)}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </Block>
            </div>



        </AppWrapper>
    )
}

const mapStateToProps = (state) => {
    return {
        authorized: state.auth.authorized,
        transactionsData: state.admin.pointsManager.pointsTransactions.data,
        transactionsDataFetched: state.admin.pointsManager.pointsTransactions.fetched,
        couponsData: state.admin.pointsManager.couponPointsTransactions.data,
        couponsDataFetched: state.admin.pointsManager.couponPointsTransactions.fetched,
        balancesData: state.admin.pointsManager.pointsBalances.data,
        balancesDataFetched: state.admin.pointsManager.pointsBalances.fetched,
        currentUserEmail: state.businessInfo.data.email,
        currentPointsBalance: state.pointsBalance.data.points,
    };
};

export default connect(mapStateToProps)(injectIntl(CSSModules(PointsTransactionsTable, styles)));